import { Transition } from "@headlessui/react";
import { JSX, useEffect } from "react";

type ToastProps = {
  showToast: boolean;
  setShowToast: React.Dispatch<React.SetStateAction<boolean>>;
  message: string | undefined;
  type?: "success" | "error" | "info" | "warning";
};

const toastConfig: Record<string, { icon: JSX.Element; bgColor: string }> = {
  success: {
    bgColor: "bg-green-700",
    icon: (
      <svg
        className="h-7 w-7"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={3}
          d="M5 13l4 4L19 7"
        />
      </svg>
    ),
  },
  error: {
    bgColor: "bg-red-700",
    icon: (
      <svg
        className="h-7 w-7"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={3}
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"
        />
      </svg>
    ),
  },
  info: {
    bgColor: "bg-blue-700",
    icon: (
      <svg
        className="h-7 w-7"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={3}
          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
    ),
  },
  warning: {
    bgColor: "bg-yellow-700",
    icon: (
      <svg
        className="h-7 w-7"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={3}
          d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
    ),
  },
};

const Toast = ({
  showToast,
  setShowToast,
  message,
  type = "info",
}: ToastProps) => {
  const { icon, bgColor } = toastConfig[type];

  useEffect(() => {
    if (showToast) {
      const timer = setTimeout(() => {
        setShowToast(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [showToast, setShowToast]);

  return (
    <Transition
      show={showToast}
      as="div"
      className="fixed right-4 top-[85px] z-50 w-[calc(100vw-32px)] sm:w-[320px]"
      enter="transform transition ease-out duration-500"
      enterFrom="translate-x-full opacity-0"
      enterTo="translate-x-0 opacity-100"
      leave="transform transition ease-in duration-300"
      leaveFrom="translate-x-0 opacity-100"
      leaveTo="translate-x-full opacity-0"
      appear={true}
    >
      <div
        className={`flex items-start justify-between gap-3 rounded-lg p-4 text-white shadow-lg ${bgColor}`}
      >
        <div className="flex items-center gap-3">
          <div className="shrink-0">{icon}</div>
          <div className="text-sm font-medium leading-tight">{message}</div>
        </div>

        {/* Close Button */}
        <button
          onClick={() => setShowToast(false)}
          className="ml-auto shrink-0 rounded-md p-0.5 transition-colors hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/50"
          aria-label="Close"
        >
          <svg
            className="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2.5}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>
    </Transition>
  );
};

export default Toast;
